// WebAuthn helpers. Core sends options as WebAuthn JSON (binary fields as base64url) and expects the
// browser's result back in the same form; the browser API itself works in ArrayBuffers.

export const passkeyErrorKeys = {
  alreadyRegistered: 'passkey.alreadyRegistered',
  cancelled: 'passkey.cancelled',
  failed: 'passkey.failed',
  notAvailable: 'passkey.notAvailable'
} as const;
export type PasskeyErrorKey =
  (typeof passkeyErrorKeys)[keyof typeof passkeyErrorKeys];

export class PasskeyError extends Error {
  readonly key: PasskeyErrorKey;

  constructor(key: PasskeyErrorKey) {
    super(key);
    this.key = key;
  }
}

export const isPasskeySupported = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.PublicKeyCredential !== 'undefined' &&
  !!navigator.credentials;

export const toBase64Url = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

export const fromBase64Url = (value: string): ArrayBuffer => {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

// The browser rejects null for optional members (an enum member of null is not a valid value).
const withoutNulls = (value: any): any => {
  if (Array.isArray(value)) {
    return value.map(withoutNulls);
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, entry]) => entry !== null && entry !== undefined)
        .map(([key, entry]) => [key, withoutNulls(entry)])
    );
  }
  return value;
};

interface JsonDescriptor {
  id: string;
  type: string;
  transports?: string[];
}

const toDescriptors = (descriptors?: JsonDescriptor[]) =>
  (descriptors ?? []).map((descriptor) => ({
    ...descriptor,
    id: fromBase64Url(descriptor.id)
  })) as PublicKeyCredentialDescriptor[];

export const toRequestOptions = (
  optionsJson: string
): PublicKeyCredentialRequestOptions => {
  const options = withoutNulls(JSON.parse(optionsJson));
  return {
    ...options,
    allowCredentials: toDescriptors(options.allowCredentials),
    challenge: fromBase64Url(options.challenge)
  };
};

export const toCreationOptions = (
  optionsJson: string
): PublicKeyCredentialCreationOptions => {
  const options = withoutNulls(JSON.parse(optionsJson));
  return {
    ...options,
    challenge: fromBase64Url(options.challenge),
    excludeCredentials: toDescriptors(options.excludeCredentials),
    user: { ...options.user, id: fromBase64Url(options.user.id) }
  };
};

export const credentialToJson = (credential: PublicKeyCredential): string => {
  const common = {
    authenticatorAttachment: credential.authenticatorAttachment ?? undefined,
    clientExtensionResults: credential.getClientExtensionResults?.() ?? {},
    id: credential.id,
    rawId: toBase64Url(credential.rawId),
    type: credential.type
  };

  if ('attestationObject' in credential.response) {
    const attestation = credential.response as AuthenticatorAttestationResponse;
    return JSON.stringify({
      ...common,
      response: {
        attestationObject: toBase64Url(attestation.attestationObject),
        clientDataJSON: toBase64Url(attestation.clientDataJSON),
        transports: attestation.getTransports?.() ?? []
      }
    });
  }

  const assertion = credential.response as AuthenticatorAssertionResponse;
  return JSON.stringify({
    ...common,
    response: {
      authenticatorData: toBase64Url(assertion.authenticatorData),
      clientDataJSON: toBase64Url(assertion.clientDataJSON),
      signature: toBase64Url(assertion.signature),
      userHandle: assertion.userHandle
        ? toBase64Url(assertion.userHandle)
        : null
    }
  });
};

export const toPasskeyError = (error: unknown): PasskeyError => {
  if (error instanceof PasskeyError) {
    return error;
  }
  const name = (error as { name?: string })?.name;
  switch (name) {
    case 'NotAllowedError':
    case 'AbortError':
      return new PasskeyError(passkeyErrorKeys.cancelled);
    case 'InvalidStateError':
      return new PasskeyError(passkeyErrorKeys.alreadyRegistered);
    case 'SecurityError':
    case 'NotSupportedError':
      return new PasskeyError(passkeyErrorKeys.notAvailable);
    default:
      return new PasskeyError(passkeyErrorKeys.failed);
  }
};

/** Runs navigator.credentials.get and returns the result as JSON for core. */
export const getPasskeyCredential = async (
  optionsJson: string
): Promise<string> => {
  if (!isPasskeySupported()) {
    throw new PasskeyError(passkeyErrorKeys.notAvailable);
  }
  try {
    const credential = (await navigator.credentials.get({
      publicKey: toRequestOptions(optionsJson)
    })) as PublicKeyCredential | null;
    if (!credential) {
      throw new PasskeyError(passkeyErrorKeys.cancelled);
    }
    return credentialToJson(credential);
  } catch (error) {
    throw toPasskeyError(error);
  }
};

/** Runs navigator.credentials.create and returns the result as JSON for core. */
export const createPasskeyCredential = async (
  optionsJson: string
): Promise<string> => {
  if (!isPasskeySupported()) {
    throw new PasskeyError(passkeyErrorKeys.notAvailable);
  }
  try {
    const credential = (await navigator.credentials.create({
      publicKey: toCreationOptions(optionsJson)
    })) as PublicKeyCredential | null;
    if (!credential) {
      throw new PasskeyError(passkeyErrorKeys.cancelled);
    }
    return credentialToJson(credential);
  } catch (error) {
    throw toPasskeyError(error);
  }
};

const browserPatterns: [RegExp, string][] = [
  [/Edg\//, 'Edge'],
  [/Firefox\//, 'Firefox'],
  [/Chrome\//, 'Chrome'],
  [/Safari\//, 'Safari']
];

const osPatterns: [RegExp, string][] = [
  [/iPhone/, 'iOS'],
  [/iPad/, 'iPadOS'],
  [/Android/, 'Android'],
  [/Mac OS X/, 'macOS'],
  [/Windows/, 'Windows'],
  [/Linux/, 'Linux']
];

const firstMatch = (patterns: [RegExp, string][], value: string) =>
  patterns.find(([pattern]) => pattern.test(value))?.[1] ?? null;

/** Browser and operating system for a default passkey name, e.g. { browser: 'Chrome', os: 'macOS' }. */
export const describeDevice = (
  userAgent: string = typeof navigator !== 'undefined'
    ? navigator.userAgent
    : ''
): { browser: string | null; os: string | null } => ({
  browser: firstMatch(browserPatterns, userAgent),
  os: firstMatch(osPatterns, userAgent)
});

/** reCAPTCHA token as a promise, for flows that need more than one guarded call. */
export type VerificationTokenProvider = () => Promise<
  string | null | undefined
>;
